<template>
    <div>
        <v-row>
            <v-col class="px-6">
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    :label="$translate(isIndividual ? 'Search person...' : 'Search team...')"
                    single-line
                    hide-details />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-expansion-panels v-model="comingMatchesExpanded">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <h3>{{$translate('Coming matches')}}, {{ comingMatches.length }} {{ $translate('pcs') }}</h3>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <teamsport-match-list :referee="referee" :matches="comingMatches"/>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            <v-col cols="12">
                <v-expansion-panels v-model="playedMatchesExpanded">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <h3>{{$translate('Played matches')}}, {{ playedMatches.length }} {{ $translate('pcs') }}</h3>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <teamsport-match-list :referee="referee" :matches="playedMatches" :showResult="true" />
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            <v-col v-if="referee" cols="12">
                <v-expansion-panels v-model="historicMatchesExpanded">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <h3>
                                {{$translate('Historic matches')}}
                                <span v-if="historicMatches">{{ computedHistoricMatches.length }} {{ $translate('pcs') }}</span>
                            </h3>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div v-if="historicMatches && !computedHistoricMatches.length && !loadingHistoricMatches">
                                <v-alert type="info" border="left" elevation="2" icon="mdi-information">
                                    {{$translate('No historic matches')}}
                                </v-alert>
                            </div>
                            <div v-else-if="historicMatches && computedHistoricMatches.length">
                                <teamsport-match-list :referee="referee" :matches="computedHistoricMatches" :showResult="true" />
                            </div>
                            <div v-else>
                                <v-btn outlined @click="getHistoricMatches" color="primary" :loading="loadingHistoricMatches" :disabled="loadingHistoricMatches">
                                    {{$translate('Load historic matches')}}
                                </v-btn>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import TeamsportMatchList from './TeamsportMatchList.vue'
export default {
    name: 'zoezi-teamsport-matches',

    props: {
        gameClassId: {
            type: Number
        },
        divisionId: {
            type: Number
        },
        isIndividual: {
            type: Boolean,
            default: false
        },
        referee: {
            type: Boolean,
            default: false
        }
    },

    components: {
        TeamsportMatchList,
    },

    watch: {
        gameClassId: {
            immediate: true,
            handler() {
                if (!this.gameClassId) return
                this.loading = true
                this.getMatches();
            }
        },
        divisionId: {
            immediate: true,
            handler() {
                if (!this.divisionId) return
                this.loading = true
                this.getMatches();
            }
        }
    },

    data: () => ({
        search: '',
        loading: false,
        matches: [],
        comingMatchesExpanded: 0,
        playedMatchesExpanded: 0,
        historicMatchesExpanded: 0,
        historicMatches: null,
        loadingHistoricMatches: false,
        unsubscribe: []
    }),

    computed: {
        comingMatches() {
            return this.matches.filter(match => !match.finished && (!match.startTime || match.startTime > this.$store.state.now30s) && this.matchMatchesSearchString(match)).sort((a, b) => {
                if (!a.startTime && !b.startTime) {
                    return 0;
                } else if (!a.startTime) {
                    return 1;
                } else if (!b.startTime) {
                    return -1;
                } else {
                    return a.startTime - b.startTime;
                }
            });
        },
        playedMatches() {
            return this.matches.filter(match => (match.finished || (match.startTime && match.startTime < this.$store.state.now30s)) && this.matchMatchesSearchString(match)).sort((a, b) => b.startTime - a.startTime);
        },
        computedHistoricMatches() {
            return this.historicMatches?.filter(match => this.matchMatchesSearchString(match)).sort((a, b) => b.startTime - a.startTime) || [];
        },
        sixMonthsAgo() {
            return new Date().addMonths(-6);
        },
    },

    methods: {
        getHistoricMatches() {
            this.loadingHistoricMatches = true;
            const args = {
                toDate: this.sixMonthsAgo.yyyymmdd(),
            }
            this.$api.get('/api/teamsport/referee/matches/get', args).then((matches) => {
                matches.forEach(match => {
                    match.startTime = Date.newFull(match.startTime);
                });
                this.historicMatches = matches;
                this.loadingHistoricMatches = false;
            }).catch(() => {
                this.loadingHistoricMatches = false;
            });
        },
        getMatches() {
            let url = ''
            let args = {}
            if(this.referee){
                url = '/api/teamsport/referee/matches/get'
                args = { fromDate: this.sixMonthsAgo.yyyymmdd() }
            } else {
                url = this.divisionId ? '/api/teamsport/division/matches/get' : '/api/teamsport/gameclass/matches/get';
                args = this.divisionId ? {id: this.divisionId} : {id: this.gameClassId};
            }

            this.$api.get(url, args).then((matches) => {
                matches.forEach(match => {
                    match.startTime = Date.newFull(match.startTime);
                });
                this.matches = matches
                this.$emit('onMatchesLoaded', matches)
                this.loading = false
            }).catch(() => {
                this.error = true
                this.loading = false
            })
        },
        matchMatchesSearchString(match) {
            if (!this.search) {
                return true;
            }

            return match.competitors.some(c => c.name.toLowerCase().includes(this.search.toLowerCase()));
        }
    },
    mounted() {
        if(this.referee){
            this.getMatches();
            this.unsubscribe.push(this.$api.subscribe({
                type: 'teamsportmatch', callback: (_, event) => {
                    if (event.data.find(x => x.referees_id?.includes(this.$store.state.user.id))) {
                        this.getMatches();
                    }
                }
            }))
        }
    },
    destroyed() {
        this.unsubscribe.forEach(u => u());
    }
}
</script>
